import { ChevronDown, ChevronRight, AlertTriangle, Check, ArrowRight, User } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { RequirementsTree } from '@ispot/shared'

/** Strip markdown syntax to plain text for display */
export function stripMarkdown(md: string): string {
  return md
    .replace(/#{1,6}\s+/g, '')
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/\*([^*]+)\*/g, '$1')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/^[-*]\s+/gm, '• ')
    .trim()
}

export interface TreeProps {
  tree: RequirementsTree
  search: string
  expandedSections: Set<string>
  toggleSection: (key: string) => void
  expandedDetail: string | null
  setExpandedDetail: (id: string | null) => void
}

export function SectionHeader({ num, title, count, isOpen, onToggle }: { num: number; title: string; count?: number; isOpen: boolean; onToggle: () => void }) {
  return (
    <div className="flex items-center justify-between px-4 py-2.5 bg-warm-50 border-b border-border-subtle cursor-pointer hover:bg-warm-100 transition-colors" onClick={onToggle}>
      <div className="flex items-center gap-2">
        {isOpen ? <ChevronDown className="w-4 h-4 text-text-muted" /> : <ChevronRight className="w-4 h-4 text-text-muted" />}
        <span className="w-5 h-5 rounded-full bg-warm-300 text-warm-700 text-[10px] font-bold flex items-center justify-center">{num}</span>
        <span className="font-semibold text-[13.5px]">{title}</span>
      </div>
      {count !== undefined && <span className="text-xs text-solution font-medium">{count} items</span>}
    </div>
  )
}

export function SourceTag({ source }: { source: 'Decided' | 'Discovered' | 'Inferred' }) {
  const colors = {
    Decided: 'bg-solution/15 text-solution',
    Discovered: 'bg-market/15 text-market',
    Inferred: 'bg-business/15 text-business',
  }
  return <span className={cn('text-[10px] font-semibold px-2 py-0.5 rounded', colors[source])}>{source}</span>
}

export function ConfidenceBar({ value, links }: { value: number; links: number }) {
  const pct = Math.round(value * 100)
  const color = value > 0.7 ? 'bg-green-500' : value > 0.4 ? 'bg-amber-500' : 'bg-red-400'
  return (
    <div className="flex items-center gap-2">
      <div className="w-12 h-1.5 rounded-full bg-warm-200 overflow-hidden">
        <div className={cn('h-full rounded-full', color)} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-[11px] text-text-muted">{pct}%</span>
    </div>
  )
}

export function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    draft: 'bg-warm-200 text-warm-600',
    validated: 'bg-green-500/10 text-green-700',
    building: 'bg-blue-500/10 text-blue-700',
    released: 'bg-green-500/15 text-green-700',
    planned: 'bg-warm-200 text-warm-600',
    'in-progress': 'bg-blue-500/10 text-blue-700',
    done: 'bg-green-500/15 text-green-700',
  }
  const labels: Record<string, string> = {
    draft: 'Draft',
    validated: 'Validated',
    building: 'Building',
    released: 'Released',
    planned: 'Planned',
    'in-progress': 'In Progress',
    done: 'Done',
  }
  return <span className={cn('text-[11px] font-medium px-2 py-0.5 rounded', styles[status] || styles.draft)}>{labels[status] || status}</span>
}

export function DetailLabel({ children, icon }: { children: React.ReactNode; icon?: React.ReactNode }) {
  return (
    <div className="text-[10px] font-semibold text-text-muted uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
      {icon}{children}
    </div>
  )
}

export function PropRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between py-1 text-[12.5px] border-b border-border-subtle last:border-0">
      <span className="text-text-muted">{label}</span>
      <span className="font-medium">{value}</span>
    </div>
  )
}

export function GenerateStep({ label, done, active }: { label: string; done: boolean; active?: boolean }) {
  return (
    <div className={cn('flex items-center gap-2', done ? 'text-green-700' : active ? 'text-solution' : 'text-text-muted')}>
      {done ? <span className="text-green-600">&#10003;</span> : active ? <span className="animate-pulse">&#9679;</span> : <span>&#9675;</span>}
      {label}
    </div>
  )
}

/** Green filled checkbox or empty bordered square */
export function CriterionCheck({ met }: { met: boolean }) {
  if (met) {
    return (
      <span className="w-4 h-4 rounded bg-green-500 flex items-center justify-center flex-shrink-0">
        <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />
      </span>
    )
  }
  return <span className="w-4 h-4 rounded border border-warm-400 flex-shrink-0" />
}

/** Horizontal use-case flow: chip → chip → chip */
export function UseCaseFlow({ steps }: { steps: string[] }) {
  if (!steps || steps.length === 0) return null
  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      {steps.map((step, i) => (
        <span key={i} className="contents">
          <span className="inline-flex items-center px-2.5 py-1 text-[12px] font-medium rounded-md border border-border bg-white text-text-secondary">
            {step}
          </span>
          {i < steps.length - 1 && <ArrowRight className="w-3 h-3 text-warm-400 flex-shrink-0" />}
        </span>
      ))}
    </div>
  )
}

/** Linked persona card shown in the PRD detail panel */
export function PersonaCard({ name, role, primaryNeed, keyPain }: { name: string; role: string; primaryNeed?: string; keyPain?: string }) {
  const initials = name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()
  return (
    <div className="border border-border-subtle rounded-lg p-4 bg-white mt-3">
      <div className="flex items-center gap-2.5 mb-2.5">
        <span className="w-8 h-8 rounded-full bg-market text-white flex items-center justify-center text-[11px] font-bold flex-shrink-0">
          {initials}
        </span>
        <div>
          <div className="text-[13px] font-semibold text-market">{name}</div>
          <div className="text-[12px] text-text-muted">{role}</div>
        </div>
      </div>
      {primaryNeed && (
        <div className="mb-1.5">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-text-muted">Primary Need</div>
          <div className="text-[12.5px] text-text-secondary">{primaryNeed}</div>
        </div>
      )}
      {keyPain && (
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-wider text-text-muted">Key Pain</div>
          <div className="text-[12.5px] text-text-secondary">{keyPain}</div>
        </div>
      )}
    </div>
  )
}
